<script>
  import Icon from "@iconify/svelte";
  import closeIcon from "@iconify/icons-mdi/close";
  import deleteIcon from "@iconify/icons-mdi/delete-outline";
  import downloadIcon from "@iconify/icons-mdi/download";
  import linkIcon from "@iconify/icons-mdi/link";

  export let apiBase = "";
  export let apiKey = "";
  export let bucket = "";
  export let file = null;
  export let detail = null;
  export let permissions = { download: true, rename: true, delete: true };
  export let onClose = () => {};
  export let onDelete = () => {};
  export let onError = () => {};
  export let onUnauthorized = () => {};

  let signedUrl = "";
  let loadingUrl = false;
  let error = "";

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function readError(res) {
    try {
      const data = await res.json();
      if (data?.error) return data.error;
    } catch {}
    return `HTTP ${res.status}`;
  }

  async function getSignedUrl() {
    if (!permissions.download) return;

    loadingUrl = true;
    error = "";

    try {
      const url = `${apiBase}/object/url?bucket=${encodeURIComponent(bucket)}&key=${encodeURIComponent(file.key)}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized("Session expired");
        return;
      }

      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      signedUrl = data.url;
    } catch (e) {
      error = e.message;
      onError(e?.message || String(e));
    } finally {
      loadingUrl = false;
    }
  }

  function downloadFile() {
    if (signedUrl) {
      window.open(signedUrl, "_blank");
    } else {
      getSignedUrl().then(() => {
        if (signedUrl) window.open(signedUrl, "_blank");
      });
    }
  }

  function getFileName(key) {
    const parts = key.split("/");
    return parts[parts.length - 1] || key;
  }

  function isImage(contentType = "", key = "") {
    if (contentType?.startsWith("image/")) return true;
    const ext = key.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif", "avidf"].includes(ext);
  }

  function isPdf(contentType = "", key = "") {
    if (contentType === "application/pdf") return true;
    return key.toLowerCase().endsWith(".pdf");
  }

  $: if (file && detail && permissions.download && isImage(detail.content_type, file.key)) {
    getSignedUrl();
  }
</script>

<div class="ui-card h-full flex flex-col">
  <!-- Header -->
  <div class="p-4 border-b border-white/10 flex items-center justify-between">
    <span class="font-medium text-white text-sm truncate flex-1 mr-2">{getFileName(file?.key || "")}</span>
    <div class="flex items-center gap-2">
      {#if permissions.download}
        <button
          type="button"
          class="p-2 hover:bg-white/10 rounded-lg transition-colors"
          onclick={downloadFile}
          title="Download"
        >
          <Icon icon={downloadIcon} width="18" height="18" />
        </button>
      {/if}
      {#if permissions.delete}
        <button
          type="button"
          class="p-2 hover:bg-white/10 rounded-lg transition-colors text-rose-400"
          onclick={onDelete}
          title="Delete"
        >
          <Icon icon={deleteIcon} width="18" height="18" />
        </button>
      {/if}
      <button
        type="button"
        class="p-2 hover:bg-white/10 rounded-lg transition-colors"
        onclick={onClose}
        title="Close"
      >
        <Icon icon={closeIcon} width="18" height="18" />
      </button>
    </div>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-y-auto p-4">
    {#if !detail}
      <div class="text-center py-8 text-zinc-500">Loading...</div>
    {:else}
      {#if error}
        <div class="mb-4 text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded-lg px-3 py-2">
          {error}
        </div>
      {/if}

      <!-- Preview (for images) -->
      {#if isImage(detail.content_type, file.key) && signedUrl}
        <div class="mb-4 rounded-lg overflow-hidden bg-black/20">
          <img src={signedUrl} alt={getFileName(file.key)} class="max-w-full max-h-48 mx-auto object-contain" />
        </div>
      {/if}

      <!-- Metadata -->
      <div class="space-y-3">
        <div class="flex justify-between">
          <span class="text-xs text-zinc-500">Size</span>
          <span class="text-xs text-white">{detail.size_human} ({detail.size?.toLocaleString()} bytes)</span>
        </div>

        <div class="flex justify-between">
          <span class="text-xs text-zinc-500">Content Type</span>
          <span class="text-xs text-white">{detail.content_type || "N/A"}</span>
        </div>

        <div class="flex justify-between">
          <span class="text-xs text-zinc-500">Last Modified</span>
          <span class="text-xs text-white">{new Date(detail.last_modified).toLocaleString()}</span>
        </div>

        <div class="flex justify-between">
          <span class="text-xs text-zinc-500">ETag</span>
          <span class="text-xs text-white font-mono truncate ml-4">{detail.etag || "N/A"}</span>
        </div>

        <div class="border-t border-white/10 pt-3">
          <span class="text-xs text-zinc-500 block mb-1">Full Path</span>
          <span class="text-xs text-zinc-300 font-mono break-all">{file.key}</span>
        </div>

        {#if signedUrl}
          <div class="border-t border-white/10 pt-3">
            <div class="flex items-center gap-2 mb-2">
              <Icon icon={linkIcon} width="14" height="14" class="text-zinc-500" />
              <span class="text-xs text-zinc-500">Signed URL (expires in 1hr)</span>
            </div>
            <input
              type="text"
              readonly
              value={signedUrl}
              class="w-full h-8 px-2 text-xs bg-black/30 border border-white/10 rounded text-zinc-400 font-mono"
              onclick={(e) => e.target.select()}
            />
          </div>
        {/if}

        {#if permissions.download && (isImage(detail.content_type, file.key) || isPdf(detail.content_type, file.key))}
          <div class="pt-3">
            <button
              type="button"
              class="ui-btn-accent w-full"
              onclick={downloadFile}
            >
              <Icon icon={downloadIcon} width="16" height="16" />
              {isPdf(detail.content_type, file.key) ? "Open PDF" : "Download"}
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>
